/**
 * Domínio comercial compartilhado entre servidor e telas (portado da DEMO-03,
 * frontend/prototipo/src/dados.ts): papéis de parceiro, regras de comissão
 * (BASE de cálculo separada da FREQUÊNCIA), condições padrão de Locação e o
 * REAJUSTE estruturado por tipo, com um texto único para Proposta, PDF, OL e
 * parceiro (uma só fonte para não divergir). Sem dependências de Node.
 */

/** Papéis de um PARCEIRO COMERCIAL. Uma mesma empresa pode ter vários papéis, sem cadastro duplicado. */
export const PAPEIS = ['Distribuidor', 'Fabricante', 'Parceiro de serviço', 'Parceiro de Locação', 'Comprador de Trade-in', 'Grupo comercial', 'Fornecedor de despesas'] as const;
export type Papel = (typeof PAPEIS)[number];
/**
 * Pacote 5 — seleção de parceiros POR PAPEL (vale na tela e no servidor). Um parceiro com vários papéis aparece onde
 * QUALQUER um dos seus papéis se qualifica: o filtro é por pertencimento, nunca exclui quem também é
 * "Fornecedor de despesas".
 */
/** Papéis comerciais: todos menos "Fornecedor de despesas" (filtro "Parceiro comercial" das Oportunidades). */
export const PAPEIS_COMERCIAIS: readonly Papel[] = PAPEIS.filter((p) => p !== 'Fornecedor de despesas');
/** Papéis de fornecimento: entidade faturadora do item/cotação da Proposta e da Ordem (OCF) substituta. */
export const PAPEIS_FORNECEDOR: readonly Papel[] = ['Distribuidor', 'Fabricante', 'Grupo comercial', 'Parceiro de serviço'];
/** O parceiro tem ao menos um dos papéis? */
export const temAlgumPapel = (papeis: readonly string[] | null | undefined, aceitos: readonly string[]) => (papeis ?? []).some((p) => aceitos.includes(p));

export type IdRegraComissao = 'por_parcela' | 'mensal_projeto' | 'unica_projeto' | 'unica_venda';
export const REGRAS_COMISSAO: { id: IdRegraComissao; nome: string; base: string; frequencia: string }[] = [
  { id: 'por_parcela', nome: 'Recorrente por parcela', base: 'Valor de cada parcela paga pelo Cliente', frequencia: 'A cada parcela recebida' },
  { id: 'mensal_projeto', nome: 'Recorrente mensalmente durante o projeto', base: 'Valor mensal do projeto', frequencia: 'Todo mês, durante o prazo' },
  { id: 'unica_projeto', nome: 'Paga uma única vez sobre o total do projeto', base: 'Valor total do projeto', frequencia: 'Uma única vez' },
  { id: 'unica_venda', nome: 'Paga uma única vez sobre o total da venda', base: 'Valor total da venda', frequencia: 'Uma única vez' },
];
export const regraComissao = (id: IdRegraComissao) => REGRAS_COMISSAO.find((r) => r.id === id)!;

/** Condições padrão do Parceiro de Locação (trazidas para a Proposta e editáveis naquela versão). */
export const CAMPOS_CONDICOES_LOCACAO = [
  { chave: 'responsabilidades', rotulo: 'Responsabilidades' },
  { chave: 'implantacao', rotulo: 'Implantação' },
  { chave: 'manutencao', rotulo: 'Manutenção' },
  { chave: 'substituicao', rotulo: 'Substituição' },
  { chave: 'seguro', rotulo: 'Seguro (quando aplicável)' },
  { chave: 'cancelamento', rotulo: 'Cancelamento' },
  { chave: 'renovacao', rotulo: 'Renovação' },
  { chave: 'reajuste', rotulo: 'Reajuste' },
  { chave: 'observacoes', rotulo: 'Observações contratuais' },
] as const;
export type CampoCondicao = (typeof CAMPOS_CONDICOES_LOCACAO)[number]['chave'];

export const TIPOS_REAJUSTE = ['Índice', 'Percentual fixo', 'Sem reajuste', 'Outro'] as const;
export type TipoReajuste = (typeof TIPOS_REAJUSTE)[number];
export const INDICES_REAJUSTE = ['IPCA', 'IGP-M', 'INPC', 'Outro'] as const;
export const PERIODICIDADES_REAJUSTE = ['Anual', 'Semestral', 'Bienal', 'A cada renovação'] as const;
export interface Reajuste {
  tipo: TipoReajuste;
  indice?: string;
  indiceOutro?: string;
  percentual?: number;
  periodicidade?: string;
  dataBase?: string;
  descricao?: string;
}
const nomeIndice = (r: Reajuste) => (r.indice === 'Outro' ? r.indiceOutro?.trim() || 'outro índice' : r.indice);
/** Texto único do reajuste (Proposta, PDF, OL, parceiro). */
export function textoReajuste(r?: Reajuste | null): string {
  if (!r) return '—';
  if (r.tipo === 'Sem reajuste') return 'Sem reajuste';
  if (r.tipo === 'Outro') return r.descricao?.trim() || 'Outro (a descrever)';
  const quando = [r.periodicidade?.toLowerCase(), r.dataBase?.trim() && `data-base/regra: ${r.dataBase.trim()}`].filter(Boolean).join(' · ');
  if (r.tipo === 'Índice') return `Índice ${nomeIndice(r) || '—'}${quando ? ` · ${quando}` : ''}`;
  return `Percentual fixo de ${(r.percentual ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%${quando ? ` · ${quando}` : ''}`;
}
/** O que falta no reajuste, conforme o TIPO (exigido no envio da Proposta de Locação). */
export function pendenciasReajuste(r?: Reajuste | null): string[] {
  if (!r) return ['Reajuste: escolha o tipo.'];
  if (r.tipo === 'Índice') return [!r.indice && 'Reajuste: escolha o índice.', r.indice === 'Outro' && !r.indiceOutro?.trim() && 'Reajuste: informe o nome do índice.', !r.periodicidade && 'Reajuste: informe a periodicidade.'].filter(Boolean) as string[];
  if (r.tipo === 'Percentual fixo') return [!(Number(r.percentual) > 0) && 'Reajuste: informe o percentual.', !r.periodicidade && 'Reajuste: informe a periodicidade.'].filter(Boolean) as string[];
  if (r.tipo === 'Outro') return r.descricao?.trim() ? [] : ['Reajuste: descreva a regra (Outro).'];
  return [];
}
/** Normaliza um reajuste recebido: só os campos do tipo escolhido; valores das listas; erro legível se inválido. */
export function normalizarReajuste(v: unknown): { ok: true; reajuste: Reajuste } | { ok: false; erro: string } {
  if (!v || typeof v !== 'object') return { ok: false, erro: 'Reajuste: escolha o tipo.' };
  const x = v as Record<string, unknown>;
  const t = (k: string) => (typeof x[k] === 'string' ? (x[k] as string).trim() : undefined);
  const tipo = t('tipo') as TipoReajuste;
  if (!TIPOS_REAJUSTE.includes(tipo)) return { ok: false, erro: `Reajuste: tipo inválido (use ${TIPOS_REAJUSTE.join(', ')}).` };
  const periodicidade = t('periodicidade') || undefined;
  if (periodicidade && !(PERIODICIDADES_REAJUSTE as readonly string[]).includes(periodicidade)) return { ok: false, erro: 'Reajuste: periodicidade inválida.' };
  if (tipo === 'Índice') {
    const indice = t('indice') || undefined;
    if (indice && !(INDICES_REAJUSTE as readonly string[]).includes(indice)) return { ok: false, erro: 'Reajuste: índice inválido.' };
    return { ok: true, reajuste: { tipo, indice, indiceOutro: indice === 'Outro' ? t('indiceOutro') || undefined : undefined, periodicidade, dataBase: t('dataBase') || undefined } };
  }
  if (tipo === 'Percentual fixo') {
    const p = x.percentual === undefined || x.percentual === null || x.percentual === '' ? undefined : Number(x.percentual);
    if (p !== undefined && (!Number.isFinite(p) || p < 0 || p >= 100)) return { ok: false, erro: 'Reajuste: percentual entre 0 e 100.' };
    return { ok: true, reajuste: { tipo, percentual: p, periodicidade, dataBase: t('dataBase') || undefined } };
  }
  if (tipo === 'Outro') return { ok: true, reajuste: { tipo, descricao: t('descricao') || undefined } };
  return { ok: true, reajuste: { tipo } };
}
